/**
 * Live Pots
 * While a hand is being bet, once someone is all-in for less than the
 * others, the pot splits into a main pot and side pots — and an all-in
 * player can only win the pots they reached. This shows each pot and who
 * can still win it. Hidden while there's only one pot.
 */

import React from 'react';
import { formatChips } from '../../utils/format';

interface LivePotsProps {
  pots: Array<{ amount: number; eligiblePlayers: string[] }>;
  players: Array<{ id: string; username: string }>;
  /** The viewer's id, shown as "You". */
  youId?: string;
}

const LivePots: React.FC<LivePotsProps> = ({ pots, players, youId }) => {
  if (pots.length < 2) return null;

  const username = new Map(players.map((p) => [p.id, p.username]));
  const everyoneCount = pots[0].eligiblePlayers.length;
  const whoCanWin = (ids: string[]) => {
    if (ids.length >= everyoneCount) return 'everyone';
    if (ids.length <= 3) return ids.map((id) => (id === youId ? 'You' : username.get(id) ?? '?')).join(' & ');
    return `${ids.length} players`;
  };

  return (
    <div data-testid="live-pots" className="p-2 sm:p-3 bg-gray-900/60 rounded-xl border border-gray-700 space-y-1">
      <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Pots</div>
      {pots.map((pot, i) => {
        const youCanWin = !!youId && pot.eligiblePlayers.includes(youId);
        return (
          <div
            key={i}
            data-testid="live-pot"
            data-amount={pot.amount}
            className={`flex items-baseline gap-1.5 text-[11px] leading-snug ${youCanWin ? '' : 'opacity-60'}`}
          >
            <span className="shrink-0 font-semibold text-white">{i === 0 ? 'Main pot' : `Side pot ${i}`}</span>
            <span className="shrink-0 text-yellow-400">{formatChips(pot.amount)}</span>
            <span className="min-w-0 text-gray-400 truncate">· {whoCanWin(pot.eligiblePlayers)}</span>
          </div>
        );
      })}
    </div>
  );
};

export default LivePots;
